// Creates a Revocation Context Provider that wraps the application and provides the revocation context to all child components
// It has a state that holds the revocation passes and a function to fetch the passes from the API

import { createContext, useCallback, useContext, useState } from 'react';
import FaceSearchStep from './validation/steps/FaceSearchStep';
import { Match, MatchStatus } from '../services/faceAuthentication/facetec/facetecProcessor';
import RetentionServiceImplementation from '../services/retention/retentionServiceImplementation';
import { RetentionService } from '../services/retention';
import { Resolver as ConfigResolver, ServiceConfig } from '../config';
import axios from 'axios';
import { isComplianceStoreCheckError } from '../services/faceAuthentication/errors';
import Log from '../common/logging';
import { fetchGknServiceData } from './GatekeeperNetworkService/GatekeeperNetworkServiceContext';

export interface RevocationProps {
  revocationPasses: Match[];
  fetchRevocationPasses(): Promise<void>;
  revokePasses(): Promise<void>;
  identityAuthToken?: string;
  setSelectedPass: (item: Match) => void;
  selectedRevocationPasses: Match[];
  revokedPasses: Match[];
}

type RevocationProviderProps = {
  children: JSX.Element;
};

export const RevocationContext = createContext<RevocationProps>({} as RevocationProps);

const revokeRequestFromMatch = (revocationPass: Match, identityAuthToken: string) => ({
  proof: { identityAuthToken, provider: 'wallet' },
  chainNetwork: revocationPass.network,
  chainType: revocationPass.chain,
  did: revocationPass.did,
  gatekeeperNetwork: revocationPass.gatekeeperNetwork,
});

// filter out passes where uniqueness is not enforced (proofOfPersonhood.faceLivenessCheck.enforceUniqueness)
const filterRevokablePasses = async (serviceConfig: ServiceConfig, records: Match[]): Promise<Match[]> => {
  const revokableRecords: Match[] = [];
  for await (const record of records) {
    const gknServiceData = await fetchGknServiceData(
      serviceConfig,
      record.gatekeeperNetwork,
      record.chain,
      record.network,
    );
    const isRevokable =
      gknServiceData?.constraints?.public?.proofOfPersonhood?.faceLivenessCheck?.enforceUniqueness || false;
    if (isRevokable) {
      revokableRecords.push(record);
    }
  }
  return revokableRecords;
};

export function RevocationProvider(props: RevocationProviderProps): JSX.Element {
  const { children } = props;
  const step = new FaceSearchStep();
  const [revocationPasses, setRevocationPasses] = useState<Match[]>([]);
  const [identityAuthToken, setIdentityAuthToken] = useState<string>();
  const revokeService: RetentionService = new RetentionServiceImplementation();
  const [selectedRevocationPasses, setSelectedRevocationPasses] = useState<Match[]>([]);
  const [revokedPasses, setRevokedPasses] = useState<Match[]>([]);
  const serviceConfig = ConfigResolver.getServiceConfig();

  const fetchRevocationPasses = async (): Promise<void> => {
    const { result } = await step.processStep();
    if (result.isFailed() && isComplianceStoreCheckError(result.failure())) {
      throw result.failure();
    } else if (result.isFailed()) {
      result.logError();
      throw new Error('Failed to fetch revocation passes');
    }

    if (result.isCancelled()) {
      // This error will be logged in the calling component
      throw new Error('Cancelled fetch revocation passes');
    }

    if (!step.data?.identityAuthToken) {
      const value = result.success();
      // Choosing to log seperately here so we can see the value of the result
      Log.error(`No identityAuthToken found in FaceSearchStep: ${JSON.stringify(value)}`);
      // This error will be logged in the calling component
      throw new Error('No identityAuthToken found');
    }

    setIdentityAuthToken(step.data?.identityAuthToken);
    // For the new flow, we need to ask the Gatekeeper for the list of dids
    // associated with the faceAuth jwt.
    const gkSearchResults = await fetchDidsFromGatekeeperForFaceIds();

    // filter out passes where uniqueness is not enforced
    const revokablePasses = await filterRevokablePasses(serviceConfig, gkSearchResults);
    setRevocationPasses(revokablePasses || []);
  };

  type GatekeeperRecordItem = {
    state: MatchStatus;
    chain: { type: string; network: string };
    did: string;
    gatekeeperNetwork: string;
    walletAddress: string;
  };

  const fetchDidsFromGatekeeperForFaceIds = async (): Promise<Match[]> => {
    if (!step.data?.identityAuthToken) {
      // The GK reads the list of faceId's from the FaceAuth identity token, so we need it here.
      return [];
    }
    const url = serviceConfig.gatekeeperFaceIdSearch.endpoint;
    const gkResult = await axios.post(url, {
      identityAuthToken: step.data.identityAuthToken,
      stateList: [MatchStatus.ACTIVE, MatchStatus.FROZEN, MatchStatus.REQUESTED],
    });
    const gatekeeperRecords = gkResult.data as GatekeeperRecordItem[];
    return gatekeeperRecords.map((item) => ({
      chain: item.chain.type,
      network: item.chain.network,
      gatekeeperNetwork: item.gatekeeperNetwork,
      did: item.did,
      status: item.state,
    }));
  };

  const revokePasses = useCallback(async (): Promise<void> => {
    if (identityAuthToken) {
      const results = selectedRevocationPasses.map(async (passToRevoke) => {
        await revokeService.revoke(revokeRequestFromMatch(passToRevoke, identityAuthToken));
        passToRevoke.status = MatchStatus.REVOKED;
        return {
          ...passToRevoke,
          status: MatchStatus.REVOKED,
        };
      });

      const confirmedResults = await Promise.all(results);
      setRevokedPasses(confirmedResults);
      setSelectedRevocationPasses([]);
    }
  }, [revocationPasses, identityAuthToken, selectedRevocationPasses]);

  // store the selected passes in state
  const setSelectedPass = useCallback(
    (item: Match) => {
      if (selectedRevocationPasses.includes(item)) {
        setSelectedRevocationPasses(selectedRevocationPasses.filter((i) => i !== item));
      } else {
        setSelectedRevocationPasses([...selectedRevocationPasses, item]);
      }
    },
    [selectedRevocationPasses, setSelectedRevocationPasses],
  );

  return (
    <RevocationContext.Provider
      value={{
        revocationPasses,
        fetchRevocationPasses,
        revokePasses,
        identityAuthToken,
        setSelectedPass,
        selectedRevocationPasses,
        revokedPasses,
      }}
    >
      {children}
    </RevocationContext.Provider>
  );
}

export const useRevocation = (): RevocationProps => {
  return useContext(RevocationContext);
};
